import LoadError from "@/components/LoadError";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, Zap, Map, Wrench, Users, GitBranch, ArrowRight, ArrowRightCircle, CheckCircle2, AlertCircle, ShieldCheck, Cpu, DollarSign, Clock, Dumbbell, Calendar, SlidersHorizontal, ChevronRight } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { Card, Disclaimer, EmptyState, Pill, Spinner, SectionTitle, useToast } from "@/components/earnos/kit";
import { Orb } from "@/components/earnos/Orb";
import { colors } from "@/lib/theme";

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-line bg-surface-2 p-4">
      <Icon size={18} className="text-brand-light" />
      <p className="text-lg font-bold text-ink mt-2">{value}</p>
      <p className="text-sm text-ink-2 mt-0.5">{label}</p>
    </div>
  );
}

function ToolRow({ icon: Icon, tint, label, sub, onClick, testID }) {
  return (
    <button onClick={onClick} data-testid={testID} className="w-full flex items-center gap-3 rounded-xl border border-line bg-surface-2 p-4 hover:border-line-strong transition-colors text-left">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: tint + "22" }}>
        <Icon size={18} style={{ color: tint }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-base font-bold text-ink">{label}</p>
        <p className="text-sm text-ink-2">{sub}</p>
      </div>
      <ChevronRight size={18} className="text-ink-2" />
    </button>
  );
}

export default function Engine() {
  const navigate = useNavigate();
  const toast = useToast();
  const [engine, setEngine] = useState(null);
  const [leverage, setLeverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setLoadError("");
    try {
      const d = await apiFetch("/engine");
      setEngine(d.engine);
      if (d.engine) {
        try { setLeverage(await apiFetch("/engine/leverage")); } catch (e) { setLoadError(e.message || "Service unavailable. Please retry."); }
      }
    } catch (e) {
      setLoadError(e.message || "Income engine unavailable.");
      toast.show(e?.message || "Failed to load", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner className="py-24" />;

  if (loadError && !loading) return <LoadError title="Income engine" message={loadError} onRetry={load} />;

  if (!engine) {
    return (
      <EmptyState
        icon={Rocket}
        title="No Income Engine yet"
        message="Complete your Economic Identity and EARNOS will design a personalized income path."
        actionTitle="Start assessment"
        onAction={() => navigate("/onboarding")}
        testID="engine-empty"
      />
    );
  }

  const rc = engine.reality_check || {};
  const tools = [
    { icon: Zap, tint: colors.info, label: "Money Moves", sub: "Your daily execution steps", onClick: () => navigate("/moves"), testID: "hub-moves" },
    { icon: Map, tint: colors.brandTertiary, label: "30-Day Plan", sub: "Rolling action plan", onClick: () => navigate("/plan"), testID: "hub-plan" },
    { icon: Wrench, tint: colors.brand, label: "Offer Studio", sub: "Package what you sell", onClick: () => navigate("/offer-builder"), testID: "hub-offers" },
    { icon: Users, tint: colors.success, label: "Leads", sub: "Track potential customers", onClick: () => navigate("/leads"), testID: "hub-leads" },
    { icon: GitBranch, tint: colors.warning, label: "Autopilot", sub: "Automation & permissions", onClick: () => navigate("/autopilot"), testID: "hub-autopilot" },
    { icon: ShieldCheck, tint: colors.success, label: "Reality Check", sub: "Evidence-based business viability", onClick: () => navigate("/reality-check"), testID: "hub-reality-check" },
    { icon: Cpu, tint: colors.brandSecondary, label: "Economic Graph", sub: "See verified work relationships", onClick: () => navigate("/economic-graph"), testID: "hub-economic-graph" },
  ];

  const fitCells = [
    ["Lifestyle fit", engine.fit?.lifestyle],
    ["Goal fit", engine.fit?.goal],
    ["Evidence", engine.fit?.evidence],
    ["Execution", engine.fit?.execution],
  ];

  return (
    <div className="animate-fade-in">
      <div className="flex flex-col items-center pt-2 pb-6">
        <Orb state="working" size={84} />
        <div className="earnos-kicker mt-3">Your Income Engine</div>
        <h1 className="text-2xl font-extrabold text-ink text-center mt-1 tracking-tight">{engine.path_name}</h1>
        {engine.is_demo ? <div className="mt-2"><Pill label="Demo data" color="#F59E0B" /></div> : null}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {engine.fit && Object.keys(engine.fit).length ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {fitCells.map(([label, val]) => {
                const v = String(val || "—");
                const good = ["Strong", "Low"].includes(v);
                const warn = ["Weak", "High", "Limited"].includes(v);
                const c = good ? colors.success : warn ? colors.warning : colors.info;
                return (
                  <div key={label} className="rounded-xl border border-line bg-surface-2 p-4">
                    <p className="text-sm font-semibold text-ink-2">{label}</p>
                    <p className="text-lg font-extrabold mt-0.5" style={{ color: c }}>{v}</p>
                  </div>
                );
              })}
            </div>
          ) : null}

          {engine.economics && (engine.economics.current || engine.economics.optimized) ? (
            <Card>
              <SectionTitle>The economics</SectionTitle>
              <div className="flex items-start gap-3 mt-3">
                <div className="flex-1">
                  <p className="earnos-kicker !text-ink-2">Current model</p>
                  {engine.economics.current ? (
                    <>
                      <p className="text-lg font-extrabold text-ink mt-1.5">{engine.economics.current.customers_needed} customers</p>
                      <p className="text-sm text-ink-3 mt-0.5">at ${engine.economics.current.price} each</p>
                      {engine.economics.current.note ? <p className="text-sm text-ink-2 mt-1.5 leading-snug">{engine.economics.current.note}</p> : null}
                    </>
                  ) : <p className="text-sm text-ink-2 mt-1.5">—</p>}
                </div>
                <ArrowRight size={18} className="text-brand-light mt-6 shrink-0" />
                <div className="flex-1 rounded-xl bg-brand/10 border border-brand/30 p-3">
                  <p className="earnos-kicker">EARNOS optimized</p>
                  {engine.economics.optimized ? (
                    <>
                      <p className="text-lg font-extrabold text-ink mt-1.5">{engine.economics.optimized.customers_needed} customers</p>
                      <p className="text-sm text-ink-3 mt-0.5">at ${engine.economics.optimized.price} · {engine.economics.optimized.offer}</p>
                      {engine.economics.optimized.why ? <p className="text-sm text-ink-2 mt-1.5 leading-snug">{engine.economics.optimized.why}</p> : null}
                    </>
                  ) : <p className="text-sm text-ink-2 mt-1.5">—</p>}
                </div>
              </div>
            </Card>
          ) : null}

          {rc && (rc.note || rc.fastest_milestone) ? (
            <Card style={{ borderColor: (rc.realistic === false ? colors.warning : colors.success) + "55" }}>
              <div className="flex items-center gap-2">
                {rc.realistic === false ? <AlertCircle size={18} className="text-warn" /> : <CheckCircle2 size={18} className="text-ok" />}
                <SectionTitle>Reality check</SectionTitle>
              </div>
              {rc.note ? <p className="text-base text-ink-3 mt-2 leading-relaxed">{rc.note}</p> : null}
              {rc.fastest_milestone ? <p className="text-base text-ink-3 mt-2"><span className="font-bold text-ink">Fastest realistic milestone: </span>{rc.fastest_milestone}</p> : null}
              {rc.longer_term_path ? <p className="text-base text-ink-3 mt-2"><span className="font-bold text-ink">Longer-term path: </span>{rc.longer_term_path}</p> : null}
            </Card>
          ) : null}

          {(engine.milestone_ladder || []).length ? (
            <Card>
              <SectionTitle>Milestone ladder</SectionTitle>
              <div className="mt-3 space-y-2">
                {engine.milestone_ladder.map((m, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-brand/20 flex items-center justify-center text-sm font-extrabold text-brand-light shrink-0">{i + 1}</div>
                    <p className="text-base text-ink-3">{m}</p>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}

          <Card>
            <SectionTitle>Why it fits you</SectionTitle>
            <div className="space-y-2 mt-3">
              {(engine.why_it_fits || []).map((w, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle2 size={18} className="text-ok mt-0.5 shrink-0" />
                  <p className="text-base text-ink-3 leading-relaxed">{w}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Right rail */}
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <Metric icon={DollarSign} label="Startup cost" value={engine.startup_cost || "—"} />
            <Metric icon={Clock} label="Time / week" value={engine.time_required || "—"} />
            <Metric icon={Dumbbell} label="Difficulty" value={engine.difficulty || "—"} />
            <Metric icon={Calendar} label="Timeline" value={engine.timeline || "—"} />
          </div>

          <Card>
            <SectionTitle>Potential monthly revenue</SectionTitle>
            <p className="text-xl font-extrabold text-ink mt-2">{engine.potential_revenue || "—"}</p>
            <p className="text-base text-ink-3 mt-2 leading-relaxed">First offer: {engine.first_offer}</p>
            <div className="flex gap-5 mt-3">
              <div className="flex-1">
                <p className="text-sm text-ink-2">Suggested price</p>
                <p className="text-lg font-bold text-ink mt-0.5">${engine.recommended_price}</p>
              </div>
              <div className="flex-1">
                <p className="text-sm text-ink-2">Customers needed</p>
                <p className="text-lg font-bold text-ink mt-0.5">{engine.customers_needed}</p>
              </div>
            </div>
            <Disclaimer text={engine.disclaimer} />
            <div className="flex flex-col gap-2 mt-4">
              <button onClick={() => navigate("/offer-builder")} data-testid="engine-build-offer" className="w-full h-11 rounded-full bg-brand text-white font-bold flex items-center justify-center gap-2 hover:brightness-110">
                <Wrench size={16} /> Build this offer in Offer Studio
              </button>
              <button onClick={() => navigate("/leads")} data-testid="engine-find-customers" className="w-full h-11 rounded-full bg-surface-3 border border-line-strong text-ink font-bold flex items-center justify-center gap-2 hover:border-brand-light transition-colors">
                <Users size={16} /> Find customers in Leads
              </button>
            </div>
          </Card>

          {leverage && leverage.stack && leverage.stack.length ? (
            <Card>
              <SectionTitle>Your leverage stack</SectionTitle>
              <p className="text-sm text-ink-2 mt-1 leading-snug">{leverage.note}</p>
              <div className="space-y-2 mt-3">
                {leverage.stack.map((s, i) => (
                  <div key={i} className="flex items-start gap-2">
                    {s.native ? <ShieldCheck size={18} className="text-ok mt-0.5 shrink-0" /> : <Cpu size={18} className="text-brand-light mt-0.5 shrink-0" />}
                    <div>
                      <p className="text-base font-semibold text-ink">{s.bottleneck}</p>
                      <p className="text-sm text-ink-2">{s.tool}{s.native ? " · in EARNOS" : `  ·  ${s.pricing}${s.mobile ? " · phone" : ""}`}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-ink-3 mt-3 leading-snug">EARNOS stays your control center — it tells you exactly what to run in each tool.</p>
            </Card>
          ) : null}
        </div>
      </div>

      <SectionTitle className="mt-8 mb-3">Engine tools</SectionTitle>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {tools.map((t) => <ToolRow key={t.label} {...t} />)}
      </div>

      <button onClick={() => navigate("/settings")} data-testid="engine-settings" className="flex items-center justify-center gap-1.5 mt-8 mx-auto text-base text-ink-2 hover:text-ink">
        <SlidersHorizontal size={16} /> Change goal, pace or regenerate engine
      </button>
    </div>
  );
}
